import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Helmet } from "react-helmet";
import { toast } from "react-toastify";

import useAuth from "../hooks/useAuth";
import { ESTADOS_DIAGNOSTICO } from "../utils/constants";
import { getClientes } from "../api/admin.api";
import {
  getDiagnosticos,
  getDiagnosticoPdfUrl,
  getDiagnosticoInvestigacionUrl,
} from "../api/diagnosticos.api";

import "./admin-analysis.css";
import "./pagination.css";

const formatDate = (value) => {
  if (!value) return "—";
  const [year, month, day] = value.split("T")[0].split("-");
  return `${day}/${month}/${year}`;
};

const AdminAnalysis = () => {
  const { logout } = useAuth();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [diagnosticos, setDiagnosticos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);
  const [showInvestigacion, setShowInvestigacion] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total_count: 0,
  });

  const [filters, setFilters] = useState({
    empresa: "",
    fechaInicio: "",
    fechaFin: "",
    fechaActualizacion: "",
    estado: "",
  });

  const clienteOptions = useMemo(() => {
    return clientes.map((cliente) => ({
      id: String(cliente.id),
      nombre: cliente.nombre_empresa,
    }));
  }, [clientes]);

  const clienteNameById = useMemo(() => {
    const map = new Map();
    clientes.forEach((cliente) => {
      map.set(Number(cliente.id), cliente.nombre_empresa);
    });
    return map;
  }, [clientes]);

  const hasActiveFilters = Object.values(filters).some(Boolean);

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      empresa: "",
      fechaInicio: "",
      fechaFin: "",
      fechaActualizacion: "",
      estado: "",
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const fetchClientes = async () => {
    try {
      const response = await getClientes({ page: 1, limit: 100 });
      setClientes(response?.items ?? []);
    } catch {
      toast.error("Error al cargar las empresas");
    }
  };

  const fetchDiagnosticos = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (filters.empresa) {
        params.cliente_id = Number(filters.empresa);
      }

      if (filters.fechaInicio) {
        params.fecha_inicio = filters.fechaInicio;
      }

      if (filters.fechaFin) {
        params.fecha_fin = filters.fechaFin;
      }

      if (filters.estado) {
        params.estado = filters.estado;
      }

      if (filters.fechaActualizacion) {
        params.fecha_actualizacion = filters.fechaActualizacion;
      }

      const response = await getDiagnosticos(params);

      setDiagnosticos(response?.items ?? []);
      setPagination((prev) => ({
        ...prev,
        total_count: response?.total_count ?? 0,
      }));
    } catch (err) {
      const message =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        "Error al cargar los diagnósticos";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientes();
  }, []);

  useEffect(() => {
    fetchDiagnosticos();
  }, [
    pagination.page,
    pagination.limit,
    filters.empresa,
    filters.fechaInicio,
    filters.fechaFin,
    filters.fechaActualizacion,
    filters.estado,
  ]);

  const rows = useMemo(() => {
    return diagnosticos.map((diag) => ({
      id: diag.id,
      identificador: diag.identificador_diagnostico,
      purchaseOrder: diag.numero_pedido || "—",
      empresa: diag.nombre_cliente || "—",
      fechaInicio: diag.fecha_inicio,
      fechaFin: diag.fecha_fin,
      fechaActualizacion: diag.fecha_actualizacion,
      estado: diag.estado,
      hasPdf: Boolean(diag.documento_pdf_url),
      hasInvestigacion: Boolean(diag.tiene_investigacion),
    }));
  }, [diagnosticos, clienteNameById]);

  const getTotalPages = () =>
    Math.max(1, Math.ceil(pagination.total_count / pagination.limit));

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > getTotalPages()) return;
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleDownloadInvestigacion = async (diagnosticoId) => {
    setDownloadingId(`inv-${diagnosticoId}`);
    try {
      const response = await getDiagnosticoInvestigacionUrl(diagnosticoId);
      if (response?.url) {
        window.open(response.url, "_blank");
      } else {
        toast.error("No se pudo obtener la URL de la investigación");
      }
    } catch {
      toast.error("Error al descargar la investigación");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadPdf = async (diagnosticoId) => {
    setDownloadingId(`pdf-${diagnosticoId}`);
    try {
      const response = await getDiagnosticoPdfUrl(diagnosticoId);
      if (response?.url) {
        window.open(response.url, "_blank");
      } else {
        toast.error("No se pudo obtener la URL del PDF");
      }
    } catch {
      toast.error("Error al descargar el análisis");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="admin-analysis-container">
      <Helmet>
        <title>BIOLAB</title>
        <meta property="og:title" content="Análisis - BIOLAB Platform" />
        <link
          rel="canonical"
          href="https://dev.portal.biolab.com.es/admin-analysis"
        />
      </Helmet>

      <div className="window admin-analysis-thq-window-elm">
        <header data-thq="thq-navbar" className="admin-analysis-thq-navbar-elm">
          <div
            data-thq="thq-navbar-nav"
            className="admin-analysis-thq-desktop-menu-elm"
          >
            <div className="admin-analysis-thq-top-elm1">
              <div className="admin-analysis-thq-menus-elm1">
                <button
                  type="button"
                  className="admin-analysis-thq-analysis-elm10 bodystandard"
                >
                  <img
                    alt="Análisis"
                    src="/menu icons/analysis.svg"
                    className="admin-analysis-thq-icon-elm10"
                  />
                  <span className="admin-analysis-thq-label-elm10 caption">
                    Análisis
                  </span>
                </button>
                <Link
                  to="/admin-report"
                  className="admin-analysis-thq-reports-elm1 bodystandard"
                >
                  <img
                    alt="Informes"
                    src="/menu icons/reports.svg"
                    className="admin-analysis-thq-icon-elm11"
                  />
                  <span className="admin-analysis-thq-label-elm11 caption">
                    Informes
                  </span>
                </Link>
                <button
                  type="button"
                  className="admin-analysis-thq-admin-elm1 bodystandard"
                >
                  <img
                    alt="Gestión"
                    src="/menu icons/admin.svg"
                    className="admin-analysis-thq-icon-elm12"
                  />
                  <span className="admin-analysis-thq-label-elm12 caption">
                    Gestión
                  </span>
                </button>
              </div>
              <div className="admin-analysis-thq-submenu-elm1">
                <Link
                  to="/admin-clients"
                  className="admin-analysis-thq-clients-elm1 bodystandard"
                >
                  <img
                    alt="Clientes"
                    src="/menu icons/clients.svg"
                    className="admin-analysis-thq-icon-elm13"
                  />
                  <span className="admin-analysis-thq-label-elm13 caption">
                    Clientes
                  </span>
                </Link>
                <Link
                  to="/admin-access"
                  className="admin-analysis-thq-access-elm1 bodystandard"
                >
                  <img
                    alt="Accesos"
                    src="/menu icons/access.svg"
                    className="admin-analysis-thq-icon-elm14"
                  />
                  <span className="admin-analysis-thq-label-elm14 caption">
                    Accesos
                  </span>
                </Link>
              </div>
            </div>
            <button
              type="button"
              className="admin-analysis-thq-exit-elm1 bodystandard"
              onClick={logout}
            >
              <img
                alt="Salir"
                src="/menu icons/exit.svg"
                className="admin-analysis-thq-icon-elm15"
              />
              <span className="admin-analysis-thq-label-elm15 caption">
                Salir
              </span>
            </button>
          </div>

          <div
            data-thq="thq-burger-menu"
            className="admin-analysis-thq-burger-menu-elm"
            onClick={() => setIsMobileMenuOpen(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setIsMobileMenuOpen(true);
            }}
          >
            <svg viewBox="0 0 1024 1024" className="admin-analysis-icon1">
              <path d="M128 554.667h768c23.552 0 42.667-19.115 42.667-42.667s-19.115-42.667-42.667-42.667h-768c-23.552 0-42.667 19.115-42.667 42.667s19.115 42.667 42.667 42.667zM128 298.667h768c23.552 0 42.667-19.115 42.667-42.667s-19.115-42.667-42.667-42.667h-768c-23.552 0-42.667 19.115-42.667 42.667s19.115 42.667 42.667 42.667zM128 810.667h768c23.552 0 42.667-19.115 42.667-42.667s-19.115-42.667-42.667-42.667h-768c-23.552 0-42.667 19.115-42.667 42.667s19.115 42.667 42.667 42.667z"></path>
            </svg>
          </div>

          <div
            data-thq="thq-mobile-menu"
            className={`admin-analysis-thq-mobile-menu-elm${
              isMobileMenuOpen ? " admin-analysis-mobile-menu-open" : ""
            }`}
          >
            <div className="admin-analysis-thq-nav-elm">
              <div className="admin-analysis-thq-content-elm1">
                <div className="admin-analysis-thq-header-elm1">
                  <img
                    alt="BIOLAB Logo"
                    src="/biolab-logo-200h.png"
                    className="admin-analysis-thq-biolablogo-elm1"
                  />
                  <div
                    data-thq="thq-close-menu"
                    className="admin-analysis-thq-close-menu-elm"
                    onClick={closeMobileMenu}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") closeMobileMenu();
                    }}
                  >
                    <img
                      alt="Cerrar"
                      src="/close.svg"
                      className="admin-analysis-thq-icon-elm16"
                    />
                  </div>
                </div>
                <div className="admin-analysis-thq-top-elm2">
                  <div className="admin-analysis-thq-menus-elm2">
                    <Link
                      to="/admin-analysis"
                      className="admin-analysis-thq-analysis-elm11 bodystandard"
                      onClick={closeMobileMenu}
                    >
                      <img
                        alt="Análisis"
                        src="/menu icons/analysis.svg"
                        className="admin-analysis-thq-icon-elm17"
                      />
                      <span className="admin-analysis-thq-label-elm16">
                        Análisis
                      </span>
                    </Link>
                    <Link
                      to="/admin-report"
                      className="admin-analysis-thq-reports-elm2 bodystandard"
                      onClick={closeMobileMenu}
                    >
                      <img
                        alt="Informes"
                        src="/menu icons/reports.svg"
                        className="admin-analysis-thq-icon-elm18"
                      />
                      <span className="admin-analysis-thq-label-elm17">
                        Informes
                      </span>
                    </Link>
                  </div>
                  <div className="admin-analysis-thq-submenu-elm2">
                    <Link
                      to="/admin-clients"
                      className="admin-analysis-thq-clients-elm2 bodystandard"
                      onClick={closeMobileMenu}
                    >
                      <img
                        alt="Clientes"
                        src="/menu icons/clients.svg"
                        className="admin-analysis-thq-icon-elm20"
                      />
                      <span className="admin-analysis-thq-label-elm19">
                        Clientes
                      </span>
                    </Link>
                    <Link
                      to="/admin-access"
                      className="admin-analysis-thq-access-elm2 bodystandard"
                      onClick={closeMobileMenu}
                    >
                      <img
                        alt="Accesos"
                        src="/menu icons/access.svg"
                        className="admin-analysis-thq-icon-elm21"
                      />
                      <span className="admin-analysis-thq-label-elm20">
                        Accesos
                      </span>
                    </Link>
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="admin-analysis-thq-exit-elm2 bodystandard"
                onClick={logout}
              >
                <img
                  alt="Salir"
                  src="/menu icons/exit.svg"
                  className="admin-analysis-thq-icon-elm22"
                />
                <span className="admin-analysis-thq-label-elm21">Salir</span>
              </button>
            </div>
          </div>
        </header>

        <div className="admin-analysis-thq-content-elm2">
          <div className="admin-analysis-thq-title-elm1">
            <h1 className="admin-analysis-thq-title-elm2">Análisis</h1>
            <img
              alt="BIOLAB Logo"
              src="/biolab-logo-200h.png"
              className="admin-analysis-thq-biolablogo-elm2"
            />
          </div>

          <div className="admin-analysis-modern-content">
            <div className="admin-analysis-modern-filters">
              <label className="admin-analysis-filter-card filter-company">
                <span className="admin-analysis-filter-label bodysmall">
                  Empresa
                </span>
                <select
                  value={filters.empresa}
                  onChange={(e) =>
                    handleFilterChange("empresa", e.target.value)
                  }
                  className="admin-analysis-filter-control bodysmall"
                >
                  <option value="">Todas las empresas</option>
                  {clienteOptions.map((empresa) => (
                    <option key={empresa.id} value={empresa.id}>
                      {empresa.nombre}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="admin-analysis-modern-filters admin-analysis-modern-filters-secondary">
              <label className="admin-analysis-filter-card filter-start-date">
                <span className="admin-analysis-filter-label bodysmall">
                  Fecha de inicio
                </span>
                <input
                  type="date"
                  value={filters.fechaInicio}
                  onChange={(e) =>
                    handleFilterChange("fechaInicio", e.target.value)
                  }
                  className="admin-analysis-filter-control bodysmall"
                />
              </label>

              <label className="admin-analysis-filter-card filter-end-date">
                <span className="admin-analysis-filter-label bodysmall">
                  Fecha de fin
                </span>
                <input
                  type="date"
                  value={filters.fechaFin}
                  onChange={(e) =>
                    handleFilterChange("fechaFin", e.target.value)
                  }
                  className="admin-analysis-filter-control bodysmall"
                />
              </label>

              <label className="admin-analysis-filter-card filter-update-date">
                <span className="admin-analysis-filter-label bodysmall">
                  Fecha de actualización
                </span>
                <input
                  type="date"
                  value={filters.fechaActualizacion}
                  onChange={(e) =>
                    handleFilterChange("fechaActualizacion", e.target.value)
                  }
                  className="admin-analysis-filter-control bodysmall"
                />
              </label>

              <label className="admin-analysis-filter-card filter-status">
                <span className="admin-analysis-filter-label bodysmall">
                  Estado
                </span>
                <select
                  value={filters.estado}
                  onChange={(e) => handleFilterChange("estado", e.target.value)}
                  className="admin-analysis-filter-control bodysmall"
                >
                  <option value="">Todos los estados</option>
                  {Object.entries(ESTADOS_DIAGNOSTICO).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {hasActiveFilters && (
              <button
                type="button"
                className="admin-analysis-clear-filters bodysmall"
                onClick={clearFilters}
              >
                Limpiar filtros
              </button>
            )}

            <div className="admin-analysis-results-head">
              <span className="subheadline">
                {loading
                  ? "Cargando análisis..."
                  : `${pagination.total_count} análisis encontrados`}
              </span>
            </div>

            {error && (
              <div className="bodysmall" style={{ color: "#b91c1c" }}>
                {error}
              </div>
            )}

            <div className="admin-analysis-table-controls">
              <label className="admin-analysis-investigacion-toggle">
                <span className="bodysmall">
                  Habilitar columna de investigación
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={showInvestigacion}
                  className={`admin-analysis-toggle-switch${showInvestigacion ? " admin-analysis-toggle-switch--on" : ""}`}
                  onClick={() => setShowInvestigacion((v) => !v)}
                />
              </label>
            </div>

            <div
              className="admin-analysis-modern-table"
              style={{
                "--table-cols": showInvestigacion
                  ? "0.9fr 1fr 1fr 1fr 1fr 1fr 0.9fr 0.9fr 0.9fr"
                  : "0.9fr 1fr 1fr 1fr 1fr 1fr 0.9fr 0.9fr",
              }}
            >
              <div className="admin-analysis-modern-header">
                <span>Identificador</span>
                <span>Purchase Order</span>
                <span>Cliente</span>
                <span>Fecha inicio</span>
                <span>Fecha fin</span>
                <span>Fecha de actualización</span>
                <span>Estado</span>
                <span>Análisis</span>
                {showInvestigacion && <span>Investigación</span>}
              </div>

              {!loading && rows.length === 0 && (
                <div className="admin-analysis-modern-row">
                  <span className="bodysmall">—</span>
                  <span className="bodysmall">—</span>
                  <span className="bodysmall">—</span>
                  <span className="bodysmall">—</span>
                  <span className="bodysmall">—</span>
                  <span className="bodysmall">—</span>
                  <span className="bodysmall">—</span>
                  <span className="bodysmall">No hay análisis</span>
                  {showInvestigacion && <span className="bodysmall">—</span>}
                </div>
              )}

              {rows.map((row) => {
                const status = ESTADOS_DIAGNOSTICO[row.estado] || {
                  label: row.estado,
                  color: "#6B7280",
                  bgColor: "#F3F4F6",
                };

                return (
                  <div key={row.id} className="admin-analysis-modern-row">
                    <span className="bodysmall">{row.identificador}</span>
                    <span className="bodysmall">{row.purchaseOrder}</span>
                    <span className="bodysmall">{row.empresa}</span>
                    <span className="bodysmall">
                      {formatDate(row.fechaInicio)}
                    </span>
                    <span className="bodysmall">
                      {formatDate(row.fechaFin)}
                    </span>
                    <span className="bodysmall">
                      {formatDate(row.fechaActualizacion)}
                    </span>
                    <div className="admin-analysis-status-cell">
                      <span
                        className="admin-analysis-status-pill bodysmall"
                        style={{
                          color: status.color,
                          backgroundColor: status.bgColor,
                        }}
                      >
                        {status.label}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="admin-analysis-download-button bodysmall"
                      disabled={
                        downloadingId === `pdf-${row.id}` || !row.hasPdf
                      }
                      onClick={() => handleDownloadPdf(row.id)}
                    >
                      <img alt="Descargar" src="/download.svg" />
                      <span>
                        {downloadingId === `pdf-${row.id}`
                          ? "Descargando..."
                          : row.hasPdf
                            ? "Descargar"
                            : "No disponible"}
                      </span>
                    </button>
                    {showInvestigacion && (
                      <button
                        type="button"
                        className="admin-analysis-download-button bodysmall"
                        disabled={
                          downloadingId === `inv-${row.id}` ||
                          !row.hasInvestigacion
                        }
                        onClick={() => handleDownloadInvestigacion(row.id)}
                      >
                        <img alt="Descargar" src="/download.svg" />
                        <span>
                          {downloadingId === `inv-${row.id}`
                            ? "Descargando..."
                            : row.hasInvestigacion
                              ? "Descargar"
                              : "No disponible"}
                        </span>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="admin-analysis-mobile-cards">
              {!loading && rows.length === 0 && (
                <div className="admin-analysis-mobile-card">
                  <span className="bodysmall">No hay análisis</span>
                </div>
              )}

              {rows.map((row) => {
                const status = ESTADOS_DIAGNOSTICO[row.estado] || {
                  label: row.estado,
                  color: "#6B7280",
                  bgColor: "#F3F4F6",
                };

                return (
                  <div key={row.id} className="admin-analysis-mobile-card">
                    <div className="admin-analysis-mobile-card-head">
                      <span className="headline03">{row.identificador}</span>
                      <span
                        className="admin-analysis-status-pill bodysmall"
                        style={{
                          color: status.color,
                          backgroundColor: status.bgColor,
                        }}
                      >
                        {status.label}
                      </span>
                    </div>
                    <div className="admin-analysis-mobile-grid">
                      <div>
                        <span className="caption">Empresa</span>
                        <span className="bodysmall">{row.empresa}</span>
                      </div>
                      <div>
                        <span className="caption">Fecha inicio</span>
                        <span className="bodysmall">
                          {formatDate(row.fechaInicio)}
                        </span>
                      </div>
                      <div>
                        <span className="caption">Fecha fin</span>
                        <span className="bodysmall">
                          {formatDate(row.fechaFin)}
                        </span>
                      </div>
                      <div>
                        <span className="caption">Actualización</span>
                        <span className="bodysmall">
                          {formatDate(row.fechaActualizacion)}
                        </span>
                      </div>
                      {showInvestigacion && (
                        <div>
                          <span className="caption">Investigación</span>
                          <span className="bodysmall">
                            {row.hasInvestigacion ? "Sí" : "No"}
                          </span>
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      className="admin-analysis-download-button bodysmall"
                      disabled={
                        downloadingId === `pdf-${row.id}` || !row.hasPdf
                      }
                      onClick={() => handleDownloadPdf(row.id)}
                    >
                      <img alt="Descargar" src="/download.svg" />
                      <span>
                        {downloadingId === `pdf-${row.id}`
                          ? "Descargando..."
                          : row.hasPdf
                            ? "Descargar análisis"
                            : "Análisis no disponible"}
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="pagination-controls">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                Anterior
              </button>
              <span>
                Página {pagination.page} de {getTotalPages()}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= getTotalPages()}
              >
                Siguiente
              </button>
              <span>Total: {pagination.total_count} análisis</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalysis;
